import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatDateTime } from '../lib/format';

type AlertRow = {
  id: number;
  name: string;
  query: string;
  case_id: string | null;
  active: number;
  unseen: number;
};

export function AlertsPage() {
  const [alerts, setAlerts] = useState<AlertRow[]>([]);
  const [hits, setHits] = useState<
    Array<{ id: number; alert_name: string; handle: string; text: string | null; date_utc: string; case_id: string | null }>
  >([]);
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [editId, setEditId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editQuery, setEditQuery] = useState('');
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    const a = await api.get<{ alerts: AlertRow[] }>('/api/investigate/alerts');
    setAlerts(a.alerts);
    const h = await api.get<{ hits: typeof hits }>('/api/investigate/alert-hits?unseen=1');
    setHits(h.hits);
  };

  useEffect(() => {
    void load().catch((e) => setError(e instanceof Error ? e.message : 'Laden mislukt'));
    const t = setInterval(() => void load().catch(() => undefined), 8000);
    return () => clearInterval(t);
  }, []);

  const create = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const res = await api.post<{ id: number; matched: number }>('/api/investigate/alerts', {
      name,
      query,
      backfill: true,
    });
    setName('');
    setQuery('');
    setInfo(`Alert aangemaakt — ${res.matched} treffer(s) in bestaande berichten.`);
    await load();
  };

  const startEdit = (a: AlertRow) => {
    setEditId(a.id);
    setEditName(a.name);
    setEditQuery(a.query);
  };

  const saveEdit = async () => {
    if (editId == null) return;
    const res = await api.patch<{ matched?: number }>(`/api/investigate/alerts/${editId}`, {
      name: editName,
      query: editQuery,
      backfill: true,
    });
    setInfo(`Alert bijgewerkt — ${res.matched ?? 0} nieuwe treffer(s) in bestaande berichten.`);
    setEditId(null);
    await load();
  };

  const remove = async (id: number) => {
    if (!confirm('Alert verwijderen? Hits verdwijnen mee.')) return;
    await api.delete(`/api/investigate/alerts/${id}`);
    await load();
  };

  const toggle = async (a: AlertRow) => {
    await api.patch(`/api/investigate/alerts/${a.id}`, { active: !a.active });
    await load();
  };

  const backfill = async (id: number) => {
    const res = await api.post<{ matched: number }>(`/api/investigate/alerts/${id}/backfill`);
    setInfo(`Terugwerkende scan: ${res.matched} nieuwe treffer(s).`);
    await load();
  };

  return (
    <div>
      <h1>Alerts</h1>
      <p className="page-lead">
        Hits op trefwoorden (AND), ook met terugwerkende kracht over bestaande berichten. Bronnen
        refreshen op de achtergrond — zie <Link to="/sources">Bronnen</Link>.
      </p>
      <form className="panel toolbar" onSubmit={(e) => void create(e)}>
        <div className="field">
          <label>Naam</label>
          <input className="input input-md" value={name} onChange={(e) => setName(e.target.value)} required />
        </div>
        <div className="field" style={{ flex: 1 }}>
          <label>Query</label>
          <input className="input" value={query} onChange={(e) => setQuery(e.target.value)} required />
        </div>
        <button className="btn" type="submit">
          Aanmaken
        </button>
      </form>
      {error && <div className="alert error">{error}</div>}
      {info && <div className="alert ok">{info}</div>}

      <div className="panel" style={{ marginTop: 12, padding: 0, overflow: 'auto' }}>
        <table className="table">
          <thead>
            <tr>
              <th>Naam</th>
              <th>Query</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {alerts.map((a) =>
              editId === a.id ? (
                <tr key={a.id}>
                  <td>
                    <input className="input" value={editName} onChange={(e) => setEditName(e.target.value)} />
                  </td>
                  <td>
                    <input className="input" value={editQuery} onChange={(e) => setEditQuery(e.target.value)} />
                  </td>
                  <td colSpan={2}>
                    <div className="row">
                      <button className="btn btn-sm" type="button" onClick={() => void saveEdit()}>
                        Opslaan
                      </button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => setEditId(null)}>
                        Annuleren
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                <tr key={a.id}>
                  <td>
                    <strong>{a.name}</strong>
                    {a.unseen > 0 && <span className="badge warn" style={{ marginLeft: 8 }}>{a.unseen} nieuw</span>}
                    {a.case_id && (
                      <div>
                        <Link to={`/cases/${a.case_id}`}>zaak</Link>
                      </div>
                    )}
                  </td>
                  <td>
                    <code>{a.query}</code>
                  </td>
                  <td>
                    <span className={`badge ${a.active ? 'ok' : 'warn'}`}>{a.active ? 'actief' : 'uit'}</span>
                  </td>
                  <td>
                    <div className="row" style={{ flexWrap: 'wrap' }}>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => startEdit(a)}>
                        Bewerken
                      </button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => void toggle(a)}>
                        {a.active ? 'Pauzeren' : 'Activeren'}
                      </button>
                      <button className="btn btn-ghost btn-sm" type="button" onClick={() => void backfill(a.id)}>
                        Opnieuw scannen
                      </button>
                      <button className="btn btn-danger btn-sm" type="button" onClick={() => void remove(a.id)}>
                        Verwijderen
                      </button>
                    </div>
                  </td>
                </tr>
              ),
            )}
            {alerts.length === 0 && (
              <tr>
                <td colSpan={4} className="empty">
                  Nog geen alerts.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <h2 style={{ fontSize: '1.05rem' }}>Ongelezen hits</h2>
      <div className="msg-list">
        {hits.map((h) => (
          <article className="msg" key={h.id}>
            <div className="msg-head">
              <span>
                {h.alert_name} · {h.handle} · {formatDateTime(h.date_utc)}
              </span>
            </div>
            <pre className="msg-text">{h.text}</pre>
          </article>
        ))}
        {hits.length === 0 && <div className="empty">Geen nieuwe hits.</div>}
      </div>
    </div>
  );
}
